import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ActiveFilters } from "../../../../commons/specs/filters";
import { ProductId, ProductType } from "../../../../commons/specs/product";
import { AvailabilityFilterLocation } from "../../../../commons/types/availability";
import {
  AvailabilityFilterVisibility,
  ProductFinderAssortmentFilterSettings,
  Settings
} from "../../../../commons/types/settings";
import { SizingBodyValues } from "../../../../commons/types/sizing";
import config from "../../config";

interface SessionSliceState {
  bodyValues: SizingBodyValues | null;
  activeFilters: ActiveFilters<ProductType>;
  showProductDetailsZoomNotice: boolean;
  availabilityFilter: {
    availabilityLocation: AvailabilityFilterLocation;
  };
  compare: {
    bookmarkId: string;
    productIds: ProductId[];
  };
}

const initialState: SessionSliceState = {
  bodyValues: null,
  activeFilters: null,
  showProductDetailsZoomNotice: true,
  availabilityFilter: {
    availabilityLocation: "dealer"
  },
  compare: {
    bookmarkId: "",
    productIds: []
  }
};

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    reset: (_, action: PayloadAction<Settings>) => {
      const availabilityFilterVisibility =
        action.payload.assortment.productFinderFilterSettings.availabilityFilterVisibility;

      return {
        ...initialState,
        availabilityFilter: {
          ...initialState.availabilityFilter,
          availabilityLocation: getInitialAvailabilityLocation(availabilityFilterVisibility)
        }
      };
    },
    setActiveFilters: (state, action: PayloadAction<ActiveFilters<ProductType>>) => {
      state.activeFilters = action.payload;
    },
    resetActiveFilters: state => {
      state.activeFilters = {};
    },
    setBodyValues: (state, action: PayloadAction<SizingBodyValues>) => {
      state.bodyValues = action.payload;
    },
    hideProductDetailsZoomNotice: state => {
      state.showProductDetailsZoomNotice = false;
    },
    setAvailabilityFilterLocation: (state, action: PayloadAction<AvailabilityFilterLocation>) => {
      state.availabilityFilter.availabilityLocation = action.payload;
    },
    updateAvailabilityFilter: (state, action: PayloadAction<ProductFinderAssortmentFilterSettings>) => {
      state.availabilityFilter.availabilityLocation = getInitialAvailabilityLocation(
        action.payload.availabilityFilterVisibility
      );
    },
    setCompare: {
      reducer: (state, action: PayloadAction<{ bookmarkId: string; productIds: ProductId[] }>) => {
        state.compare.bookmarkId = action.payload.bookmarkId;
        state.compare.productIds = action.payload.productIds;
      },
      prepare: (bookmarkId: string, productIds: ProductId[]) => ({
        payload: { bookmarkId, productIds }
      })
    },
    clearCompare: state => {
      state.compare.bookmarkId = initialState.compare.bookmarkId;
      state.compare.productIds = initialState.compare.productIds;
    },
    addProductIdToCompare: (state, action: PayloadAction<ProductId>) => {
      if (
        state.compare.productIds.length < config.shared.compareMaxLimit &&
        !state.compare.productIds.includes(action.payload)
      ) {
        state.compare.productIds.push(action.payload);
      }
    },
    removeProductIdFromCompare: (state, action: PayloadAction<ProductId>) => {
      state.compare.productIds = state.compare.productIds.filter(productId => productId !== action.payload);
    }
  }
});

function getInitialAvailabilityLocation(visibility: AvailabilityFilterVisibility): AvailabilityFilterLocation {
  const visibilityToLocationMap: Record<AvailabilityFilterVisibility, AvailabilityFilterLocation> = {
    dealer_only: "dealer",
    supplier_only: "supplier",
    both_default_dealer: "dealer",
    both_default_everywhere: "everywhere"
  };

  return visibilityToLocationMap[visibility];
}

/**
 * Widens product-specific filters to the Redux store's union type.
 *
 * This widening is mathematically sound: ActiveFilters<Bicycle> is structurally
 * a valid member of ActiveFilters<ProductType>, but TypeScript cannot prove this
 * due to how conditional types interact with generic constraints.
 *
 * TypeScript issue: When Type extends a union (ProductType = Bicycle | Motorcycle),
 * the compiler cannot prove that FilterValue<Bicycle, K> is assignable to
 * FilterValue<Bicycle | Motorcycle, K> because FilterValue uses conditional types
 * that depend on the Type parameter.
 *
 * @see https://github.com/microsoft/TypeScript/issues/13995
 * @see https://github.com/microsoft/TypeScript/issues/57299
 */
export function toStoreFilters<Type extends ProductType>(filters: ActiveFilters<Type>): ActiveFilters<ProductType> {
  return filters as ActiveFilters<ProductType>;
}

export default sessionSlice;
